import Decimal from 'decimal.js'
import {
    PaymentContract,
    CustomerPaymentAccountDetailContract,
    CustomerLookupContract,
    GiftCardDetailContract,
    PaymentSettingContract,
    LoyaltyRedeemPointRuleSettingContract,
    OrderDetailContract,
} from 'models'
import { createContext, useContext } from 'react'
import {
    CalculatorSwitchMode,
    IPaymentTypeInfo,
} from '../../CalculationInput/CalculatePanel'
import { CustomerAccountPaymentModel } from '../dialogs/CustomerAccountsSellPopup/CustomerAccountsPaymentMobile'
import { PaymentDisplaySection } from './SectionType'
import { PaymentFormModel } from '../dialogs/splitPaymentDialog/SplitPaymentContext'
import { PaymentResult } from '../Models/PaymentResult'
import { PaymentIntegrationProcessStatus } from '../Models/Enum'
import { PaymentPayload } from './usePaymentDialog'
import { PickRequired } from 'utilities/typeUtility/typeUtility'

export interface SignType {
    Increase: '+'
    Decrease: '-'
}
export interface IPaymentTypeSelection
    extends PickRequired<
            PaymentSettingContract,
            'paymentSettingID' | 'paymentTypeID' | 'customLogoUrl' | 'name'
        >,
        Pick<
            PaymentSettingContract,
            | 'roundOffTotalsToNearest'
            | 'isCashRoundingEnabled'
            | 'customPaymentTypeID'
        > {
    disabled: (amount: number) => boolean
}
export interface IPaymentContext {
    isReadyToProceedPayment: boolean
    paymentTypeLists: IPaymentTypeSelection[]
    isProcessing: boolean
    paidPayments: PaymentContract[]
    orderTotalDue: Decimal
    selectedPaymentType: PaymentSettingContract | undefined
    quickButtons: number[]
    payment: PaymentPayload | undefined
    expectAmount: Decimal
    tenderedAmount: Decimal
    loadingCustomerPaymentAccount: boolean
    paymentAccounts: CustomerPaymentAccountDetailContract[]
    selectedCustomerAccount: CustomerAccountPaymentModel | undefined
    paymentDetail: IPaymentTypeInfo | undefined
    paymentAccountCustomerID: string | undefined
    currentSection: PaymentDisplaySection
    selectedGiftCard: GiftCardDetailContract | undefined
    paymentForms: PaymentFormModel[]
    onSelectGiftCard: (giftCard: GiftCardDetailContract | undefined) => void
    onSetDisplaySection: (section: PaymentDisplaySection) => void
    onSetTenderedAmount: (amount: number) => void
    onSetExpectAmount: (amount: Decimal, paymentType?: number) => void
    onSelectPaymentType: (paymentTypeID: string) => void
    onOpenAccountDialog: () => void
    onCloseAccountDialog: () => void
    onSubmitPayment: () => Promise<string | undefined>
    onSelectCustomerAccount: (
        customerAccount: CustomerAccountPaymentModel,
    ) => void
    customerList: CustomerLookupContract[]
    orderCustomerID: string | undefined
    onSelectedPaymentCustomerID: (paymentCustomerId: string) => void
    currencyID: number
    onChangePaymentForms: (paymentForms: PaymentFormModel[]) => void
    // Tips
    tipsAmount: Decimal
    addTipsMode: boolean
    onEnterAddTipsMode: () => void
    onExitAddTipsMode: () => void
    onSetTipsAmount: (amount: Decimal, mode: CalculatorSwitchMode) => void
    tipsType: CalculatorSwitchMode
    clearTips: () => void
    tipsRate: Decimal
    isSplitPayment: boolean
    onQuickSplitGiftCardPayment: (
        giftcardDetail: GiftCardDetailContract,
    ) => void
    defaultPaymentType: PaymentSettingContract | undefined
    noQuickTip: boolean
    //redeem point
    showRedeemPointForm: boolean
    onSetShowRedeemPointForm: (selected: boolean) => void
    pointRedeem: number | undefined
    redeemDiscount: number | undefined
    pointRedemptionSetting: LoyaltyRedeemPointRuleSettingContract | undefined
    onRedeemSubmit: (
        pointToRedeem: number,
        discount: number,
        newTotalDue: number,
    ) => void
    orderDetail: OrderDetailContract | undefined
    onClickCustomer?: () => void
    isAddTipsEnabled: boolean
    onSetReceivedCash: (amount: number) => void

    paymentIntegrationProcessStatus?: PaymentIntegrationProcessStatus
    resetPaymentStatus: () => void
    evoPaymentTransactionId?: string
    checkEvoPaymentStatus?: (transactionId: string) => void
    isCheckingEvoPaymentStatus?: boolean
    resetEvoPaymentDialogState?: () => void

    //use for split payment (Evo)
    paymentIntegrationPaymentID?: string | undefined
    onCloseDialogWithReset: () => void
    onSetSplitPayment: (value: boolean) => void
    isSaleComplete: boolean
    changeValue: Decimal
    useSplitPayment: boolean
    errorMessage: string
    additionalInstruction: string
    isNegativeTotalDue: boolean
    hasAwaitingProcessTransaction?: boolean
}

export const emptySale: () => PaymentResult = () => ({
    saleID: undefined,
    orderID: undefined,
    payment: [],
    totalDueAmount: 0,
    cashRoundingAdjustAmount: 0,
})
const initialState: IPaymentContext = {
    isReadyToProceedPayment: false,
    paymentTypeLists: [],
    isProcessing: false,
    paidPayments: [],
    orderTotalDue: new Decimal(0),
    loadingCustomerPaymentAccount: false,
    paymentAccounts: [],
    selectedCustomerAccount: undefined,
    quickButtons: [0],
    currencyID: 0,
    selectedPaymentType: undefined,
    payment: undefined,
    expectAmount: new Decimal(0),
    tenderedAmount: new Decimal(0),
    paymentDetail: undefined,
    customerList: [],
    orderCustomerID: undefined,
    paymentAccountCustomerID: undefined,
    selectedGiftCard: undefined,
    currentSection: PaymentDisplaySection.Calculation,
    paymentForms: [],
    noQuickTip: false,
    redeemDiscount: undefined,
    onSelectGiftCard: (giftCard: GiftCardDetailContract | undefined) => {
        throw new Error('Function not implemented.')
    },
    onSetDisplaySection: (section: PaymentDisplaySection) => {
        throw new Error('Function not implemented.')
    },
    onSetExpectAmount: (amount: Decimal, paymentType?: number) => {
        throw new Error('Function not implemented.')
    },
    onSelectCustomerAccount: (customerAccount: CustomerAccountPaymentModel) => {
        throw new Error('Function not implemented.')
    },
    onOpenAccountDialog: () => {
        throw new Error('Function not implemented.')
    },
    onCloseAccountDialog: () => {
        throw new Error('Function not implemented.')
    },
    onSelectPaymentType: (paymentTypeID: string) => {
        throw new Error('Function not implemented.')
    },
    onSubmitPayment: () => {
        throw new Error('Function not implemented.')
    },
    onSetTenderedAmount: (amount: number) => {
        throw new Error('Function not implemented.')
    },
    onSelectedPaymentCustomerID: (paymentCustomerID: string) => {
        throw new Error('Function not implemented.')
    },
    //redeem point
    showRedeemPointForm: false,
    pointRedeem: undefined,
    pointRedemptionSetting: undefined,
    onChangePaymentForms: (paymentForms: PaymentFormModel[]) => {
        throw new Error('Function not implemented.')
    },
    onSetShowRedeemPointForm: (selected: boolean) => {
        throw new Error('Function not implemented.')
    },
    orderDetail: undefined,
    onRedeemSubmit: (
        pointToRedeem: number,
        discount: number,
        newTotalDue: number,
    ) => {
        throw new Error('Function not implemented.')
    },
    onClickCustomer: () => {
        throw new Error('Function not implemented.')
    },
    // Tips
    tipsAmount: new Decimal(0),
    addTipsMode: false,
    onEnterAddTipsMode: () => {
        throw new Error('Function not implemented.')
    },
    onExitAddTipsMode: () => {
        throw new Error('Function not implemented.')
    },
    onSetTipsAmount: (tipsAmount: Decimal, mode: CalculatorSwitchMode) => {
        throw new Error('Function not implemented.')
    },
    tipsType: CalculatorSwitchMode.Custom,
    clearTips: () => {
        throw new Error('Function not implemented.')
    },
    tipsRate: new Decimal(0),
    isSplitPayment: false,
    onQuickSplitGiftCardPayment: () => {},
    defaultPaymentType: undefined,
    isAddTipsEnabled: true,
    onSetReceivedCash: (amount: number) => {
        throw new Error('Function not implemented.')
    },

    resetPaymentStatus: () => {},
    evoPaymentTransactionId: '',
    checkEvoPaymentStatus: (transactionId: string) => {},
    isCheckingEvoPaymentStatus: false,
    resetEvoPaymentDialogState: () => {},

    //use for split payment (Ev0)
    paymentIntegrationPaymentID: undefined,
    onCloseDialogWithReset: () => {
        throw new Error('Function not implemented.')
    },
    onSetSplitPayment: () => {
        throw new Error('Function not implemented.')
    },
    isSaleComplete: false,
    changeValue: new Decimal(0),
    useSplitPayment: false,
    errorMessage: '',
    additionalInstruction: '',
    isNegativeTotalDue: false,
    hasAwaitingProcessTransaction: false,
}
const AppContext = createContext(initialState)
const { Provider } = AppContext

export { Provider as PaymentProvider }

export default function usePaymentContext() {
    const appValue = useContext(AppContext)
    return appValue
}
